import type { JSX } from 'react';

import { colors } from '../../colors';
import { u } from '../../styles/unit';
import { InfoIcon, WarningIcon } from '../icons/index';
import { Inline, Stack } from '../Layout';
import { Box, BoxProps } from '../Layout/Box';
import { Heading3, Text } from '../Text';

import * as css from './StatusBox.css';

import type { StatusBoxVariants } from './StatusBox.css';

type Action = {
    handleAction: () => void;
    label: string;
};

type StatusBoxProps = {
    title: string;
    description: string;
    action?: Action;
} & Omit<BoxProps, 'className'> &
    StatusBoxVariants;

export const StatusBox = ({ title, description, centerAbsolute, status, action, ...rest }: StatusBoxProps) => {
    const hasAction = !!action;
    return (
        <Box {...rest} className={css.statusBox({ centerAbsolute, status })}>
            <Stack p={u(1.5)}>
                <Inline
                    width="100%"
                    pt={u(1)}
                    pl={u(1)}
                    pr={hasAction ? 0 : u(1)}
                    pb={hasAction ? 0 : u(1)}
                    gap={u(2)}
                    alignItems="center"
                >
                    <RenderIcon status={status} hasAction={hasAction} />
                    <Stack width="100%">
                        <Heading3>{title}</Heading3>
                        <Text variant="p">{description}</Text>
                    </Stack>
                </Inline>
                <RenderAction status={status} action={action} />
            </Stack>
        </Box>
    );
};

type RenderIconProps = { status: StatusBoxVariants['status']; hasAction: boolean };
function RenderIcon({ status, hasAction }: RenderIconProps) {
    const size = hasAction ? u(8) : u(6);
    if (status === 'error') {
        return <WarningIcon color={colors.red[200]} size={size} />;
    }
    if (status === 'info') {
        return <InfoIcon color={colors.blue[200]} size={size} />;
    }
    return null;
}

type RenderActionProps = { status: StatusBoxVariants['status']; action?: Action };
function RenderAction({ status, action }: RenderActionProps): JSX.Element | null {
    if (!action) {
        return null;
    }

    return (
        <Inline width="100%" pt={u(2)} justifyContent="flex-end">
            <button className={css.ghostButton({ status })} onClick={action.handleAction}>
                <Text variant="h4">{action.label}</Text>
            </button>
        </Inline>
    );
}
